import { useMemo, useState, ReactElement, useCallback } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import cn from 'classnames'

import {
  PageLayout,
  Button,
  useGoogleMaps,
  mapMode as mapModes,
  safetyCaseHazardValuesToSafetyCaseHazardUpdateUpsert,
  safetyCaseHazardToSafetyCaseHazardValues,
  showSuccessAlert,
  showErrorAlert,
  ISafetyCaseHazardValues,
  ISafetyCaseHazardUpsertValues,
} from '../../../components'
import SafetyCaseHazardBuilder from './safety-case-hazard-builder'

import { useSafetyCaseHazardByIdQuery, useUpdateSafetyCaseHazardMutation } from '../../../services'
import { useAppContext } from '../../../app-context'
import { isDeepEqual } from '../../../util'
import { claims, useAccessControl } from '../../../security'

import { IGeoJson } from '../../../interfaces'
import { useBlocker } from '../../../hooks'

const UpdateSafetyCaseHazardPage = () => {
  const navigate = useNavigate()

  const { hasAccess } = useAccessControl()

  const hasSafetyCaseWriteAccess = useMemo(() => hasAccess([claims.safetyCase.write]), [hasAccess])

  const { dispatch: appDispatch } = useAppContext()

  const { safetyCaseId, hazardId } = useParams()

  const { setMapMode } = useGoogleMaps()

  const [hazardGeoJson, setHazardGeoJson] = useState<IGeoJson | undefined>()

  const hazardQuery = useSafetyCaseHazardByIdQuery(
    { id: Number(hazardId) },
    {
      enabled: hazardId !== undefined && hazardId !== null,
      onSuccess: hazard => {
        setMapMode(mapModes.CLEAR)
        setHazardGeoJson(hazard?.geoJson)
      },
    },
  )

  const { data: hazard, refetch: refetchHazard, isFetching: isHazardFetching } = hazardQuery

  const location = useLocation()

  const [headerControls, setHeaderControls] = useState<ReactElement | undefined>(undefined)

  const [backSearch] = useState((location?.state as any)?.backSearch)

  const _headerControls = useMemo(
    () => (
      <div>
        <Button
          onClick={() => navigate({ pathname: `/safety-cases/${safetyCaseId}/hazards`, search: backSearch })}
          className={cn(hasSafetyCaseWriteAccess && 'mr-2')}
          type="secondary"
        >
          Back To Case Hazards
        </Button>

        {hasSafetyCaseWriteAccess && headerControls}
      </div>
    ),
    [hasSafetyCaseWriteAccess, headerControls, navigate, backSearch, safetyCaseId],
  )

  const { mutateAsync: updateHazard, isLoading: isHazardUpdating } = useUpdateSafetyCaseHazardMutation({})

  const isSaveDisabled = useMemo(() => isHazardFetching || isHazardUpdating, [isHazardFetching, isHazardUpdating])

  const saveHazardInitialValues = useMemo(() => safetyCaseHazardToSafetyCaseHazardValues(hazard), [hazard])

  const [saveHazardValues, setSaveHazardValues] = useState<ISafetyCaseHazardUpsertValues | undefined>(
    saveHazardInitialValues,
  )

  const isHazardChanged = useMemo(() => {
    return (
      !isHazardFetching &&
      ((saveHazardValues ? !isDeepEqual(saveHazardInitialValues, saveHazardValues) : false) ||
        !isDeepEqual(hazardGeoJson, hazard?.geoJson || undefined))
    )
  }, [saveHazardValues, saveHazardInitialValues, hazardGeoJson, hazard, isHazardFetching])

  const isSaveButtonDisabled = useMemo(() => isSaveDisabled || !isHazardChanged, [isSaveDisabled, isHazardChanged])

  const [saveHazardForm, setSaveHazardForm] = useState<any>()

  const handleUpdateHazard = useCallback(
    async (hazardValues: ISafetyCaseHazardValues) => {
      if (!hazard) {
        return
      }

      let hazardUpdateUpsert = safetyCaseHazardValuesToSafetyCaseHazardUpdateUpsert(hazardValues, {
        safetyCaseHazard: hazard,
        geoJson: hazardGeoJson,
      })

      if (!hazardUpdateUpsert) {
        return
      }

      try {
        await updateHazard(hazardUpdateUpsert)
      } catch (err) {
        appDispatch(showErrorAlert('Error updating hazard'))
        console.warn(err)
        return
      }

      setMapMode(mapModes.CLEAR)
      appDispatch(showSuccessAlert('Hazard successfully updated'))
      refetchHazard()
    },
    [appDispatch, hazard, updateHazard, refetchHazard, hazardGeoJson, setMapMode],
  )

  // Prompt user if they try to leave the page with unsaved changes
  useBlocker(
    useCallback(
      tx => {
        if (isHazardChanged && hasSafetyCaseWriteAccess) {
          const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave this page?')
          if (confirmLeave) {
            // Reset the form values to initial values if the user confirms leaving
            setSaveHazardValues(saveHazardInitialValues)
            tx.retry()
          }
        } else {
          tx.retry()
        }
      },
      [isHazardChanged, saveHazardInitialValues, hasSafetyCaseWriteAccess],
    ),
  )

  const saveHeaderControls = useMemo(() => {
    return (
      <Button
        disabled={isSaveButtonDisabled && isSaveDisabled}
        onClick={() => {
          saveHazardForm?.submit()
        }}
      >
        Save
      </Button>
    )
  }, [isSaveButtonDisabled, isSaveDisabled, saveHazardForm])

  const pageTitle = useMemo(
    () => `Safety Hazard ${hazardQuery?.data?.name ? `- ${hazardQuery?.data?.name}` : ''}`,
    [hazardQuery?.data?.name],
  )

  return (
    <PageLayout title={pageTitle} headerControls={_headerControls}>
      <SafetyCaseHazardBuilder
        hasWriteAccess={hasSafetyCaseWriteAccess}
        hazardQuery={hazardQuery}
        setHeaderControls={setHeaderControls}
        handleSaveHazard={handleUpdateHazard}
        saveHeaderControls={saveHeaderControls}
        setHazardGeoJson={setHazardGeoJson}
        hazardGeoJson={hazardGeoJson}
        setSaveHazardForm={setSaveHazardForm}
        saveHazardInitialValues={saveHazardInitialValues}
        setSaveHazardValues={setSaveHazardValues}
        isSaveFormDisabled={isSaveDisabled}
      />
    </PageLayout>
  )
}

export default UpdateSafetyCaseHazardPage
